import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';

export interface GuidedTourStorage {
  hasSeen: (tourId: string) => boolean;
  markSeen: (tourId: string) => void;
}

export const GUIDED_TOUR_SEEN_KEY_PREFIX = 'nr.guidedTour.seen.';

/** What each tour was seen, remembered in this browser. Blocked storage just forgets it. */
export function createLocalGuidedTourStorage(storage: Storage | undefined): GuidedTourStorage {
  return {
    hasSeen(tourId: string): boolean {
      try {
        return storage?.getItem(`${GUIDED_TOUR_SEEN_KEY_PREFIX}${tourId}`) === 'true';
      } catch {
        return false;
      }
    },
    markSeen(tourId: string): void {
      try {
        storage?.setItem(`${GUIDED_TOUR_SEEN_KEY_PREFIX}${tourId}`, 'true');
      } catch {
        // The tour may show again, which is better than breaking the page.
      }
    },
  };
}

function getLocalStorage(): Storage | undefined {
  try {
    return inject(DOCUMENT).defaultView?.localStorage;
  } catch {
    return undefined;
  }
}

export const GUIDED_TOUR_STORAGE = new InjectionToken<GuidedTourStorage>(
  'Where the seen guided tours are kept',
  {
    providedIn: 'root',
    factory: (): GuidedTourStorage => createLocalGuidedTourStorage(getLocalStorage()),
  },
);

/** Lets a single tour be open at a time, and each one be shown only until the user has seen it. */
@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  private readonly storage = inject(GUIDED_TOUR_STORAGE);
  private readonly seenInThisVisit = new Set<string>();
  private holder: object | undefined;

  /** Whether `owner` may show the tour: the user has not seen it yet and no other tour is open. */
  claim(tourId: string, owner: object): boolean {
    if (this.hasSeen(tourId) || (this.holder && this.holder !== owner)) return false;
    this.holder = owner;
    return true;
  }

  /** `owner` is going away before its tour ended. */
  release(owner: object): void {
    if (this.holder === owner) this.holder = undefined;
  }

  /** The user finished or skipped the tour, so it is not shown again. */
  complete(tourId: string, owner: object): void {
    this.release(owner);
    this.seenInThisVisit.add(tourId);
    this.storage.markSeen(tourId);
  }

  private hasSeen(tourId: string): boolean {
    return this.seenInThisVisit.has(tourId) || this.storage.hasSeen(tourId);
  }
}
